import React, { useState, useEffect, useRef } from "react";
import { Grid, TextField, CircularProgress } from "@mui/material";
import vendorService from "@/modules/vendor/service/vendor.int.service";

const GooglePlacesAutocomplete = ({ onAddressSelect, prefilledAddress = "" }) => {
  const [selectedAddress, setSelectedAddress] = useState(prefilledAddress);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [placePredictions, setPlacePredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const debounceTimeout = useRef(null); // Debounce timeout reference
  const abortController = useRef(new AbortController()); // AbortController for canceling API calls

  useEffect(() => {
    setSelectedAddress(prefilledAddress);
  }, [prefilledAddress]);

  const handleInputChange = async (event) => {
    event.preventDefault();
    const value = event.target.value;
    setSelectedAddress(value);

    // If input length is less than 3, do not trigger the API call
    if (value.length <= 1) {
      setIsMenuVisible(false);
      return;
    }

    // Clear the previous timeout if it exists
    clearTimeout(debounceTimeout.current);

    // Set a new timeout to call the API after 2 seconds of inactivity
    debounceTimeout.current = setTimeout(async () => {
      // Cancel the previous request if it exists
      abortController.current.abort();

      // Create a new AbortController for the new request
      abortController.current = new AbortController();

      setIsMenuVisible(true);
      setIsLoading(true);

      try {
        // Make the API call and pass the signal to handle cancellation
        const response = await vendorService.getPlacesAutocomplete(value, {
          signal: abortController.current.signal,
        });
        setPlacePredictions(response.predictions);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching place predictions:", error);
        }
      } finally {
        setIsLoading(false);
      }
    }, 500);
  };

  const handleItemClick = async (e, place) => {
    e.preventDefault();

    try {
      const response = await vendorService.getPlaceDetails(place.place_id);
      const placeDetails = response.result;
      if (placeDetails) {
        const location = placeDetails.geometry.location;
        const addressComponents = placeDetails.address_components;
        const cityComponent = addressComponents.find(component =>
          component.types.includes("locality")
        );
        const city = cityComponent ? cityComponent.long_name : "";
        const postalCode = addressComponents.find(component =>
          component.types.includes("postal_code")
        );

        const selectedLocation = {
          address: placeDetails.formatted_address, // ✅ Ensure 'address' is included
          latitude: location.lat,
          longitude: location.lng,
          city: city,
          postalCode: postalCode ? postalCode.long_name : "",
        };

        setSelectedAddress(selectedLocation.address);
        setIsMenuVisible(false);
        onAddressSelect(selectedLocation);
      }
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  };

  return (
    <Grid item xs={12} sm={0}>
      <TextField
        fullWidth
        name="address"
        value={selectedAddress}
        onChange={handleInputChange}
        placeholder="Search for a place"
        variant="outlined"
        sx={{
          backgroundColor: "rgba(211, 211, 211, 0.18)",
          "& .MuiOutlinedInput-root": {
            backgroundColor: "rgba(211, 211, 211, 0.23)",
            borderRadius: "12px",
          },
        }}
      />

      {isLoading && (
        <CircularProgress size={24} sx={{ position: "absolute", top: 10, right: 12 }} />
      )}

      {isMenuVisible && (
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            marginTop: "8px",
            maxHeight: "200px",
            overflowY: "auto",
            position: "relative",
          }}
        >
          {placePredictions.map((place) => (
            <div
              key={place.place_id}
              style={{
                padding: "8px 12px",
                backgroundColor: "#f0f0f0",
                cursor: "pointer",
                borderRadius: "8px",
                transition: "background-color 0.2s ease",
              }}
              onClick={(e) => handleItemClick(e, place)}
            >
              {place.description}
            </div>
          ))}
        </div>
      )}
    </Grid>
  );
};

export default GooglePlacesAutocomplete;
